import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

interface Category {
  id: number;
  name_ar: string;
  slug: string;
}

interface Subcategory {
  id: number;
  name_ar: string;
}

interface Service {
  id: number;
  name_ar: string;
}

interface Region {
  id: number;
  name_ar: string;
}

interface City {
  id: number;
  name_ar: string;
}

interface AgeCategory {
  id: number;
  name_ar: string;
}

const CreateAdPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [categories, setCategories] = useState<Category[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [adType, setAdType] = useState<'livestock' | 'service'>('livestock');
  const [serviceId, setServiceId] = useState('');
  const [serviceScope, setServiceScope] = useState('');
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [ageCategories, setAgeCategories] = useState<AgeCategory[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [ageCategoryId, setAgeCategoryId] = useState('');
  const [price, setPrice] = useState('');
  const [priceType, setPriceType] = useState('fixed');
  const [quantity, setQuantity] = useState('1');
  const [gender, setGender] = useState('female');
  const [weight, setWeight] = useState('');
  const [ageMonths, setAgeMonths] = useState('');
  const [regionId, setRegionId] = useState('');
  const [cityId, setCityId] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [showPhone, setShowPhone] = useState(true);
  const [responsibilityAccepted, setResponsibilityAccepted] = useState(false);
  const [deliveryAvailable, setDeliveryAvailable] = useState(false);
  const [deliveryCityId, setDeliveryCityId] = useState('');
  const [slaughterAvailable, setSlaughterAvailable] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState('');
  const [slaughterFee, setSlaughterFee] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [maxImages, setMaxImages] = useState(10);
  const [videoCfg, setVideoCfg] = useState<{ enabled: boolean; maxDur: number; maxMb: number }>({ enabled: false, maxDur: 30, maxMb: 20 });

  useEffect(() => {
    api.get('/app-config').then((r) => {
      const a = r.data?.data?.ads ?? {};
      if (a.max_images) setMaxImages(Number(a.max_images));
      setVideoCfg({ enabled: !!a.video_enabled, maxDur: Number(a.video_max_duration_sec ?? 30), maxMb: Number(a.video_max_size_mb ?? 20) });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // التوكن صار في كوكي HttpOnly (غير مقروء من JS) — نعتمد على حالة المصادقة
    if (!authLoading && !isAuthenticated) {
      toast.error('يجب تسجيل الدخول أولاً');
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // بوابة: التأكد من إكمال بيانات النشاط قبل السماح بنشر إعلان
  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/profile/seller-profile')
      .then((res) => {
        const d = res.data?.data;
        if (!d) return;
        if (d.is_seller === false) {
          toast.error('كن بائعاً أولاً قبل نشر إعلان');
          navigate('/my/seller-profile?redirect=/ads/create');
        } else if (d.is_complete === false) {
          toast.error('أكمل بيانات نشاطك أولاً قبل نشر إعلان');
          navigate('/my/seller-profile?redirect=/ads/create');
        }
      })
      .catch(() => { /* عند التعذّر نترك الخادم يفرض البوابة عند الإرسال */ });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, regRes, svcRes] = await Promise.all([
          api.get('/categories'),
          api.get('/regions'),
          api.get('/services'),
        ]);
        setCategories(catRes.data.data || []);
        setRegions(regRes.data.data || []);
        setServices(svcRes.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (categoryId) {
      api.get(`/categories/${categoryId}/subcategories`).then((res) => {
        setSubcategories(res.data.data || []);
      }).catch(() => setSubcategories([]));

      api.get(`/categories/${categoryId}/age-categories`).then((res) => {
        setAgeCategories(res.data.data || []);
      }).catch(() => setAgeCategories([]));
    } else {
      setSubcategories([]);
      setAgeCategories([]);
      setSubcategoryId('');
      setAgeCategoryId('');
    }
  }, [categoryId]);

  useEffect(() => {
    if (regionId) {
      api.get(`/regions/${regionId}/cities`).then((res) => {
        setCities(res.data.data || []);
      }).catch(() => setCities([]));
    } else {
      setCities([]);
      setCityId('');
    }
  }, [regionId]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (images.length + files.length > maxImages) {
      toast.error('الحد الأقصى ' + maxImages + ' صور');
      return;
    }
    setImages((prev) => [...prev, ...files]);
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !description || !price || !cityId || (adType === 'service' ? !serviceId : !categoryId)) {
      toast.error('أكمل جميع الحقول المطلوبة');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('type', adType);
      if (adType === 'service') {
        formData.append('service_id', serviceId);
        if (serviceScope) formData.append('address', serviceScope);
      } else {
        formData.append('category_id', categoryId);
        if (subcategoryId) formData.append('subcategory_id', subcategoryId);
        if (ageCategoryId) formData.append('age_category_id', ageCategoryId);
        formData.append('quantity', quantity);
        formData.append('gender', gender);
        if (weight) formData.append('weight', weight);
        if (ageMonths) formData.append('age_months', ageMonths);
        formData.append('delivery_available', deliveryAvailable ? '1' : '0');
        if (deliveryAvailable && deliveryCityId) formData.append('delivery_city_id', deliveryCityId);
        if (deliveryAvailable && deliveryFee) formData.append('delivery_fee', deliveryFee);
        formData.append('slaughter_available', slaughterAvailable ? '1' : '0');
        if (slaughterAvailable && slaughterFee) formData.append('slaughter_fee', slaughterFee);
      }
      formData.append('price', price);
      formData.append('price_type', priceType);
      formData.append('city_id', cityId);
      formData.append('contact_phone', contactPhone || user?.phone || '');
      formData.append('show_phone', showPhone ? '1' : '0');
      formData.append('disclaimer_accepted', '1');

      images.forEach((img, idx) => {
        formData.append(`images[${idx}]`, img);
      });

      const res = await api.post('/ads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newId = res.data?.data?.id;
      if (videoFile && newId) {
        const vf = new FormData();
        vf.append('video', videoFile);
        try { await api.post(`/ads/${newId}/video`, vf, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
      }

      toast.success('تم إضافة الإعلان بنجاح');
      navigate(`/ads/${newId || ''}`);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      // بوابة الخادم: عضو يحتاج ترقية، أو بائع بيانات نشاطه ناقصة → توجيه للصفحة
      if (errors?.upgrade_required || errors?.seller_profile_incomplete) {
        toast.error(errors?.upgrade_required ? 'كن بائعاً أولاً قبل نشر إعلان' : 'أكمل بيانات نشاطك أولاً قبل نشر إعلان');
        navigate('/my/seller-profile?redirect=/ads/create');
        return;
      }
      // حدود النشر (يومي/أسبوعي/أقصى إعلانات نشطة) — الرسالة النصّية تأتي في message
      if (errors?.limit_reached) {
        toast.error(err.response?.data?.message || 'تجاوزت حدّ النشر المسموح.');
        return;
      }
      if (errors) {
        const firstError = Object.values(errors)[0];
        toast.error(Array.isArray(firstError) ? firstError[0] as string : (err.response?.data?.message || 'حدث خطأ'));
      } else {
        toast.error(err.response?.data?.message || 'حدث خطأ');
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="max-w-site mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">إضافة إعلان جديد</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* نوع الإعلان */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">نوع الإعلان</h2>
          <div className="grid grid-cols-2 gap-4">
            <button type="button" onClick={() => setAdType('livestock')}
              className={`border-2 rounded-xl p-4 text-right transition ${adType === 'livestock' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="font-bold text-gray-800">🐑 مواشي</div>
              <div className="text-xs text-gray-500 mt-1">بيع أو شراء المواشي بمختلف أنواعها</div>
            </button>
            <button type="button" onClick={() => setAdType('service')}
              className={`border-2 rounded-xl p-4 text-right transition ${adType === 'service' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="font-bold text-gray-800">🧰 خدمات</div>
              <div className="text-xs text-gray-500 mt-1">نقل، بيطرة، أعلاف، حضائر وغيرها</div>
            </button>
          </div>
        </div>

        {/* المعلومات الأساسية */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">المعلومات الأساسية</h2>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">عنوان الإعلان *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="مثال: نعجة نجدية للبيع"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              maxLength={100}
            />
          </div>

          {videoCfg.enabled && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">فيديو الإعلان (اختياري) — حتى {videoCfg.maxDur}ث · {videoCfg.maxMb}MB</label>
              <input
                type="file"
                accept="video/mp4,video/quicktime"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f && f.size > videoCfg.maxMb * 1024 * 1024) { toast.error('حجم الفيديو يتجاوز ' + videoCfg.maxMb + 'MB'); return; }
                  setVideoFile(f);
                }}
                className="w-full text-sm text-gray-600"
              />
              {videoFile && <p className="text-xs text-gray-500 mt-1">📹 {videoFile.name}</p>}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">الوصف *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="اكتب وصف تفصيلي للإعلان..."
              rows={5}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              maxLength={2000}
            />
            <p className="text-xs text-gray-400 mt-1">{description.length}/2000</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {adType === 'service' ? (
              <div>
                <label className="block text-sm text-gray-600 mb-2">نوع الخدمة *</label>
                <select
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">اختر نوع الخدمة</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.name_ar}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm text-gray-600 mb-2">التصنيف *</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">اختر التصنيف</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name_ar}</option>
                  ))}
                </select>
              </div>
            )}

            {subcategories.length > 0 && (
              <div>
                <label className="block text-sm text-gray-600 mb-2">السلالة</label>
                <select
                  value={subcategoryId}
                  onChange={(e) => setSubcategoryId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">اختر السلالة</option>
                  {subcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>{sub.name_ar}</option>
                  ))}
                </select>
              </div>
            )}

            {ageCategories.length > 0 && (
              <div>
                <label className="block text-sm text-gray-600 mb-2">النوع</label>
                <select
                  value={ageCategoryId}
                  onChange={(e) => setAgeCategoryId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">اختر النوع</option>
                  {ageCategories.map((age) => (
                    <option key={age.id} value={age.id}>{age.name_ar}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* المواصفات والسعر */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">المواصفات والسعر</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">السعر (ر.س) *</label>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">نوع السعر</label>
              <select
                value={priceType}
                onChange={(e) => setPriceType(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="fixed">ثابت</option>
                <option value="negotiable">قابل للتفاوض</option>
                <option value="auction">مزاد</option>
              </select>
            </div>

            {adType === 'service' && (
              <div>
                <label className="block text-sm text-gray-600 mb-2">نطاق الخدمة</label>
                <input
                  type="text"
                  value={serviceScope}
                  onChange={(e) => setServiceScope(e.target.value)}
                  placeholder="مثال: الرياض والمنطقة الوسطى"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            {adType !== 'service' && (
            <div>
              <label className="block text-sm text-gray-600 mb-2">الكمية (رأس)</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="1"
              />
            </div>
            )}

            {adType !== 'service' && (
            <div>
              <label className="block text-sm text-gray-600 mb-2">الجنس</label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="female">أنثى</option>
                <option value="mixed">مختلط</option>
              </select>
            </div>
            )}

            {adType !== 'service' && (
            <div>
              <label className="block text-sm text-gray-600 mb-2">الوزن (كجم)</label>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="اختياري"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>
            )}

            {adType !== 'service' && (
            <div>
              <label className="block text-sm text-gray-600 mb-2">العمر (شهر)</label>
              <input
                type="number"
                value={ageMonths}
                onChange={(e) => setAgeMonths(e.target.value)}
                placeholder="اختياري"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="0"
              />
            </div>
            )}
          </div>
        </div>

        {/* الموقع */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">الموقع</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">المنطقة *</label>
              <select
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">اختر المنطقة</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>{r.name_ar}</option>
                ))}
              </select>
            </div>

            {cities.length > 0 && (
              <div>
                <label className="block text-sm text-gray-600 mb-2">المدينة *</label>
                <select
                  value={cityId}
                  onChange={(e) => setCityId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">اختر المدينة</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>{c.name_ar}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* خيارات إضافية */}
        {adType !== 'service' && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">خيارات إضافية</h2>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={deliveryAvailable} onChange={(e) => setDeliveryAvailable(e.target.checked)}
                className="w-5 h-5 mt-0.5 text-primary-500 border-gray-300 rounded focus:ring-primary-500" />
              <div>
                <span className="text-sm font-semibold text-gray-700">🚚 التوصيل متاح</span>
                <p className="text-xs text-gray-500 mt-0.5">فعّلها إذا كنت توصّل الطلب للمشتري</p>
              </div>
            </label>

            {deliveryAvailable && (
              <div className="pr-8">
                <label className="block text-sm text-gray-600 mb-2">مدينة التوصيل</label>
                <select value={deliveryCityId} onChange={(e) => setDeliveryCityId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">اختر مدينة التوصيل</option>
                  {cities.map((c) => <option key={c.id} value={c.id}>{c.name_ar}</option>)}
                </select>
                {cities.length === 0 && <p className="text-[11px] text-gray-400 mt-1">اختر المنطقة أولاً لعرض المدن</p>}
                <label className="block text-sm text-gray-600 mt-3 mb-2">رسوم التوصيل</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setDeliveryFee('')}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border ${deliveryFee === '' ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-200'}`}>مجاني</button>
                  <button type="button" onClick={() => setDeliveryFee(deliveryFee || '0')}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border ${deliveryFee !== '' ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-200'}`}>برسوم</button>
                </div>
                {deliveryFee !== '' && (
                  <div className="relative mt-2">
                    <input type="number" min="0" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} placeholder="0"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">ر.س</span>
                  </div>
                )}
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={slaughterAvailable} onChange={(e) => setSlaughterAvailable(e.target.checked)}
                className="w-5 h-5 mt-0.5 text-primary-500 border-gray-300 rounded focus:ring-primary-500" />
              <div>
                <span className="text-sm font-semibold text-gray-700">🔪 الذبح متاح</span>
                <p className="text-xs text-gray-500 mt-0.5">فعّلها إذا توفّر خدمة الذبح للمشتري</p>
              </div>
            </label>

            {slaughterAvailable && (
              <div className="pr-8">
                <label className="block text-sm text-gray-600 mb-2">رسوم الذبح</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setSlaughterFee('')}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border ${slaughterFee === '' ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-200'}`}>مجاني</button>
                  <button type="button" onClick={() => setSlaughterFee(slaughterFee || '0')}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border ${slaughterFee !== '' ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-200'}`}>برسوم</button>
                </div>
                {slaughterFee !== '' && (
                  <div className="relative mt-2">
                    <input type="number" min="0" value={slaughterFee} onChange={(e) => setSlaughterFee(e.target.value)} placeholder="0"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">ر.س</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* التواصل */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">التواصل</h2>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">رقم التواصل</label>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value.replace(/\D/g, ''))}
              placeholder={user?.phone || '05XXXXXXXX'}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              dir="ltr"
              maxLength={10}
            />
            <p className="text-xs text-gray-400 mt-1">اتركه فاضي لاستخدام رقمك المسجل</p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showPhone}
              onChange={(e) => setShowPhone(e.target.checked)}
              className="w-4 h-4 text-primary-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-600">إظهار رقم الجوال في الإعلان</span>
          </label>
        </div>

        {/* إقرار المسؤولية */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={responsibilityAccepted}
              onChange={(e) => setResponsibilityAccepted(e.target.checked)}
              className="w-5 h-5 mt-0.5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-semibold text-gray-700">إقرار المسؤولية *</span>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                أقر بأن جميع المعلومات المدخلة صحيحة وأنني المسؤول الوحيد عن محتوى هذا الإعلان.
                وأتعهد بأن المواشي المعروضة سليمة وخالية من الأمراض المعدية.
              </p>
            </div>
          </label>
        </div>

        {/* الصور */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-4">الصور</h2>
          <p className="text-xs text-gray-400 mb-4">أضف حتى 10 صور. الصورة الأولى ستكون الصورة الرئيسية.</p>

          <div className="flex flex-wrap gap-3 mb-4">
            {previews.map((src, idx) => (
              <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                >
                  ✕
                </button>
                {idx === 0 && (
                  <span className="absolute bottom-0 left-0 right-0 bg-primary-500 text-white text-center text-xs py-0.5">رئيسية</span>
                )}
              </div>
            ))}

            {images.length < 10 && (
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 transition">
                <span className="text-2xl text-gray-400">+</span>
                <span className="text-xs text-gray-400">إضافة صورة</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* زر الإرسال */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-primary-500 text-white py-3.5 rounded-xl font-semibold hover:bg-primary-600 transition disabled:opacity-50 text-lg"
          >
            {loading ? 'جاري النشر...' : 'نشر الإعلان'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-8 bg-gray-100 text-gray-600 py-3.5 rounded-xl font-semibold hover:bg-gray-200 transition"
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAdPage;
